import { createSignal, createEffect, onCleanup, onMount, untrack, on, Show } from "solid-js";
import { Portal } from "solid-js/web";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import * as d3 from "d3";
import L from "leaflet";

import Loading from "../../utilities/Loading";

import { persistantStore } from "../../../store/persistantStore";
const { selectedClassIcon } = persistantStore;

import { filtered, tooltip, setTooltip, phase, color, normalized, eventType, tabledata } from "../../../store/globalStore";
import { MANEUVER_EVENT_TYPES } from "../../../utils/maneuverEventType";
import { selectedEvents, setSelectedEvents, setLegendSelectionActive, triggerUpdate, setTriggerUpdate, triggerSelection, setTriggerSelection, setHasSelection, setSelection, cutEvents, isCut, isEventHidden, selectedRange, legendSelectionActive, highlightedEventIds } from "../../../store/selectionStore";
import { selectedGradesManeuvers, setSelectedGradesManeuvers, selectedSources } from "../../../store/filterStore";
import { maneuverPlayheadSeconds } from "../../../store/playbackStore";

import { putData } from "../../../utils/global";
import { error as logError, debug, log } from "../../../utils/console";
import { buildColorGrouping } from "../../../utils/colorGrouping";
import { resolveItemColor, UNSELECTED_TRACK_COLOR } from "../../../utils/selectionColors";
import { assignYearEventMetadataOnPoint } from "../../../utils/maneuverMetadataFields";
import { fetchMapData } from "../../../services/maneuversDataService";
import { getManeuversConfig } from "../../../utils/maneuversConfig";
import { apiEndpoints } from "../../../config/env";
import { user } from "../../../store/userStore";
import { sourcesStore } from "../../../store/sourcesStore";
import { shouldBlockGradeLikeHotkeysForPerformanceSelections } from "../../../utils/performanceLocalSelection";
import { maneuverBaselineStore } from "../../../store/maneuverBaselineStore";
import { baselineTrackCssClass } from "../../../utils/maneuverBaselineTracks";
import type { ManeuverBaselineMapPoint } from "../../../utils/maneuverBaselineTracks";
import {
  collectFiniteLatLngs,
  positiveEventIdsFromTracks,
  notifyManeuverMapRecentered,
  shouldRecenterManeuverMap,
} from "../../../utils/maneuverMapRecenter";

import "../../../styles/thirdparty/mapbox-gl.css";
import "leaflet/dist/leaflet.css";

function escapeSelectorId(id: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(id);
  }
  return id.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

export interface ManeuverMapProps {
  context?: "dataset" | "historical" | "fleet";
  onDataUpdate?: () => void;
  /** Solo ManeuverWindow / multi-window: unique Leaflet root id and scoped D3 (avoids duplicate #maneuver-map). */
  instanceId?: string;
  /** Live ALL grid: per-cell maneuver type instead of global eventType. */
  maneuverEventType?: string;
  /** Live ALL grid: event ids for this cell instead of global filtered(). */
  eventList?: number[];
  /** Live ALL grid: table rows for metadata lookup in this cell. */
  tableRows?: Record<string, unknown>[];
  /** Hide footer label in compact grid cells. */
  compact?: boolean;
  /** Live solo panels: when false, skip triggerUpdate-driven fetches while hidden. */
  panelActive?: () => boolean;
  /** Live poll gate: notify parent when map fetch loading state changes. */
  onLoadStateChange?: (loading: boolean) => void;
}

/** Point along a maneuver track (map + metadata). */
export interface MapTrackPoint {
  event_id?: number;
  time?: number;
  LatLng?: { lat: number; lng: number };
  tws_bin?: number;
  vmg_perc_avg?: number;
  tack?: string;
  State?: string;
  Config?: string;
  source_name?: string;
  race?: string | number;
  date?: string;
  hdg?: number;
  lat?: number;
  lng?: number;
  twa?: number;
  json?: { values: Record<string, unknown>[] };
  [key: string]: unknown;
}

type ManeuverPathRecord = {
  eventId: number;
  firstPoint: MapTrackPoint;
  points: MapTrackPoint[];
};

type LeafletMap = {
  getContainer(): HTMLElement;
  getPanes(): { overlayPane: HTMLElement };
  latLngToLayerPoint(latlng: { lat: number; lng: number }): { x: number; y: number };
  getZoom(): number;
  fitBounds(bounds: unknown): void;
  on(type: string, fn: (e: unknown) => void): unknown;
  once(type: string, fn: (e: unknown) => void): unknown;
  remove(): void;
  invalidateSize(): void;
  getBounds(): {
    getSouth(): number;
    getNorth(): number;
    getWest(): number;
    getEast(): number;
  };
  _loaded?: boolean;
  _container?: HTMLElement | null;
  _crosstrackListenersAttached?: boolean;
};

type GetItemColorFn = (item: MapTrackPoint) => string;

// Helper function to sanitize time values for use in CSS selectors
// Replaces problematic characters (negative signs, dots, etc.) with safe alternatives
// CSS identifiers must start with a letter, underscore, or non-ASCII character
// and can contain letters, digits, hyphens, underscores, and non-ASCII characters
/** Boat positions are drawn every 5 seconds along the track */
const BOAT_INTERVAL_SEC = 5;

/**
 * Class for the boat drawn at the timeseries playhead time. Must NOT contain the `boat` token —
 * DrawMap/doUpdates blanket-remove `.boat` and `.boat-circle`, and the interval boats auto-fade.
 */
const PLAYHEAD_BOAT_CLASS = "boat-playhead";

/** Don't draw a playhead boat when the nearest track sample is further away than this. */
const PLAYHEAD_MATCH_TOLERANCE_SEC = 2;

/** Returns [prevPoint, point] pairs for drawing boats at most every BOAT_INTERVAL_SEC, based on point.time (seconds). */
function getBoatPositionsByTime(points: MapTrackPoint[]): [MapTrackPoint, MapTrackPoint][] {
  const result: [MapTrackPoint, MapTrackPoint][] = [];
  if (!points?.length) return result;
  let lastBoatTime = -Infinity;
  for (let idx = 0; idx < points.length; idx++) {
    const d = points[idx];
    const t = typeof d?.time === "number" && Number.isFinite(d.time) ? d.time : (d?.time != null ? Number(d.time) : NaN);
    const timeSec = Number.isFinite(t) ? t : idx * 0.5; // fallback ~2Hz if time missing
    if (idx === 0 || timeSec >= lastBoatTime + BOAT_INTERVAL_SEC) {
      const prev = idx > 0 ? points[idx - 1] : d;
      result.push([prev, d]);
      lastBoatTime = timeSec;
    }
  }
  if (points.length > 0) {
    const last = points[points.length - 1];
    const secondLast = points.length > 1 ? points[points.length - 2] : last;
    if (result.length === 0 || result[result.length - 1][1] !== last) {
      result.push([secondLast, last]);
    }
  }
  return result;
}

const sanitizeTimeForId = (time: string | number | null | undefined): string => {
  // Handle null, undefined, or empty values
  if (time === null || time === undefined || time === '') {
    return '0';
  }
  
  const timeStr = String(time);
  
  // If the string is empty after conversion, return '0'
  if (timeStr.trim() === '') {
    return '0';
  }
  
  // Replace negative sign at the start with 'neg' prefix
  let sanitized = timeStr.startsWith('-') 
    ? 'neg' + timeStr.substring(1)
    : timeStr;
  
  // Replace dots with 'dot'
  sanitized = sanitized.replace(/\./g, 'dot');
  
  // Replace any remaining problematic characters (non-alphanumeric except underscore and hyphen) with underscore
  // But ensure we don't have consecutive hyphens or hyphens at the start
  sanitized = sanitized.replace(/[^a-zA-Z0-9_-]/g, '_');
  
  // Ensure it starts with a letter or underscore (CSS identifier requirement)
  // If it starts with a digit, prefix with 't'
  if (/^[0-9]/.test(sanitized)) {
    sanitized = 't' + sanitized;
  }
  
  // Replace consecutive hyphens with single hyphen
  sanitized = sanitized.replace(/-+/g, '-');
  
  // Remove leading/trailing hyphens
  sanitized = sanitized.replace(/^-+|-+$/g, '');
  
  // If empty after all processing, return '0'
  if (sanitized === '') {
    return '0';
  }
  
  return sanitized;
};

// Function to get boat path based on class icon type and zoom level
const getBoatPathForZoom = (zoomLevel: number): string => {
  const iconValue = selectedClassIcon();
  const iconType = (iconValue && typeof iconValue === 'string' && iconValue.trim() !== '') 
    ? iconValue.trim().toLowerCase() 
    : 'monohull';
  
  if (iconType === 'multihull') {
    // Multihull paths scaled for different zoom levels, centered at (0,0)
    if (zoomLevel > 18) {
      // Full size multihull (2x wider), centered at (0,0) - goes from y=-12 to y=12
      return "M0 12 L5.332 0 L5.332 6 L6.665 12 L8 6 L8 -12 L-8 -12 L-8 6 L-6.665 12 L-5.332 6 L-5.332 0 Z";
    } else if (zoomLevel > 14) {
      // Medium size multihull (58% scale), centered at (0,0)
      return "M0 7 L3.109 0 L3.109 3.5 L3.888 7 L4.666 3.5 L4.666 -7 L-4.666 -7 L-4.666 3.5 L-3.888 7 L-3.109 3.5 L-3.109 0 Z";
    } else if (zoomLevel > 10) {
      // Small size multihull (25% scale), centered at (0,0)
      return "M0 3 L1.333 0 L1.333 1.5 L1.666 3 L2 1.5 L2 -3 L-2 -3 L-2 1.5 L-1.666 3 L-1.333 1.5 L-1.333 0 Z";
    }
  }
  
  // Default monohull paths for different zoom levels
  if (zoomLevel > 18) {
    return "M0 -12 L-4 -12 L-4 0 L-2 8 L0 12 L2 8 L4 0 L4 -12 Z";
  } else if (zoomLevel > 14) {
    return "M0 -7 L-2 -7 L-2 0 L-1 4 L0 7 L1 4 L2 0 L2 -7 Z";
  } else if (zoomLevel > 10) {
    return "M0 -3 L-1 -3 L-1 0 L-0.5 1.5 L0 3 L0.5 1.5 L1 0 L1 -3 Z";
  }
  
  return ""; // Empty path for very low zoom
};

export default function ManeuverMap(props?: ManeuverMapProps) {
  // Get context from props, default to 'dataset' for backward compatibility
  const context = props?.context || 'dataset';
  const maneuverMapElementId = () =>
    props?.instanceId ? `${props.instanceId}-maneuver-map` : "maneuver-map";
  const d3MapPane = () => d3.select(`#${escapeSelectorId(maneuverMapElementId())}`);
  void getManeuversConfig(context);
  const [mapdata, setMapData] = createSignal<MapTrackPoint[][]>([]);
  type ColorGroup = { key: string | number; items: MapTrackPoint[] };
  const [, setColorGroups] = createSignal<ColorGroup[]>([]);
  const [, setGroupRepIds] = createSignal<Set<number>>(new Set<number>());
  const [description, setDescription] = createSignal("");
  const [loading, setLoading] = createSignal(true);
  const [firstload, setFirstLoad] = createSignal(true);
  const [mapInitialized, setMapInitialized] = createSignal(false);

  const isPanelActive = (): boolean => {
    if (!props) return false;
    const fn = props.panelActive;
    return fn ? fn() : true;
  };

  createEffect(() => {
    if (!props || !isPanelActive()) return;
    props.onLoadStateChange?.(loading());
  });

  let map: LeafletMap | null = null;
  let svg: d3.Selection<SVGSVGElement, unknown, any, any> | undefined;
  let g: d3.Selection<SVGGElement, unknown, any, any> | undefined;
  let updateInProgress = false;
  let zoom_level = 13;
  let latlngarray: { lat: number; lng: number }[] = [];
  let firstDraw = true;
  let lastEventType: string | null = null;
  let lastPhase: string | null = null;
  let shouldCenterOnNextDraw = false;
  /** Event ids on screen when the maneuver type changed. A draw of only these must not fit. */
  let recenterStaleEventIds: Set<number> | null = null;
  let centerToken = 0;
  let centerTimeout: ReturnType<typeof setTimeout> | null = null;
  // Track all boat fade timeouts so we can clear them on phase change
  let boatFadeTimeouts: NodeJS.Timeout[] = [];

  let mapWidth = 910;
  let mapHeight = 600; // Default height, will be updated to fill container
  let maneuverPathsData: ManeuverPathRecord[] = [];

  // Raw map-geometry responses keyed by event_id, scoped to project+eventType+description.
  // Geometry is immutable per event within a scope, so live merge polls only fetch new ids.
  const GEOMETRY_CACHE_MAX = 8000;
  const geometryCache = new Map<number, Record<string, any>>();
  let geometryCacheScope = '';

  // Using global color scale for consistency
  let myColorScale: any = d3.scaleLinear();
  let getItemColor: GetItemColorFn | null = null; // Store getItemColor function for SOURCE coloring
  let minVal = 9999999
  let maxVal = -9999999

  const resolvedEventType = () => (props?.maneuverEventType || eventType() || '').toUpperCase();

  const resolvedEventIds = (): number[] => {
    if (props?.eventList != null) {
      return props.eventList.filter((id): id is number => typeof id === 'number' && id > 0);
    }
    const currentFiltered = filtered();
    if (!currentFiltered || !Array.isArray(currentFiltered)) return [];
    return currentFiltered
      .map((item: unknown) =>
        typeof item === 'number' ? item : (item as { event_id?: number })?.event_id ?? item
      )
      .filter((id): id is number => typeof id === 'number' && id > 0);
  };

  const resolvedTableRows = (): Record<string, unknown>[] => {
    if (props?.tableRows != null) return props.tableRows;
    return (tabledata() as Record<string, unknown>[]) ?? [];
  };

  const resolvedFilteredSnapshot = (): unknown[] => {
    if (props?.eventList != null) return props.eventList;
    return filtered() as unknown[];
  };

  const isEventSelected = (id: number) => highlightedEventIds().includes(id);
  const isFiltered = (id: number) => resolvedEventIds().includes(id);

  const getDescription = () => {
    const currentPhase = phase();
    const isNormalized = normalized();
    
    if (currentPhase == 'FULL') {
      if (isNormalized) {
        setDescription('0_Normalized')
      } else {
        setDescription('0_Standard')
      }
    } else if (currentPhase == 'INVESTMENT') {
      if (isNormalized) {
        setDescription('1_Normalized')
      } else {
        setDescription('1_Standard')
      }
    } else if (currentPhase == 'TURN') {
      if (isNormalized) {
        setDescription('2_Normalized')
      } else {
        setDescription('2_Standard')
      }
    } else if (currentPhase == 'ACCELERATION') {
      if (isNormalized) {
        setDescription('3_Normalized')
      } else {
        setDescription('3_Standard')
      }
    } else {
      // Default fallback - use FULL phase description if phase is not set or unknown
      if (isNormalized) {
        setDescription('0_Normalized')
      } else {
        setDescription('0_Standard')
      }
    }
  }

  // Async function to fetch data
  const fetchData = async () => {
    try {
      // Get the current filtered event list - map endpoint requires non-empty event_list
      const currentFiltered = resolvedFilteredSnapshot();
      const eventIds = resolvedEventIds();
      const hasEvents = eventIds.length > 0;
      
      // Log fetch attempt for fleet context
      if (context === 'fleet') {
        log(`[FleetManeuvers] fetchData called - filtered().length: ${currentFiltered?.length || 0}, hasEvents: ${hasEvents}, mapInitialized: ${mapInitialized()}, map exists: ${!!map}`);
      }
      
      // Map endpoint requires event_list to be non-empty, so we must have events
      if (!hasEvents) {
        // No events yet (e.g. table still loading or no events selected) – skip map fetch for now
        // Only log if map is initialized (to avoid spam during initial load)
        if (mapInitialized() && map) {
          if (context === 'fleet') {
            log(`[FleetManeuvers] Skipping fetch - no events in filtered list (length: ${currentFiltered?.length || 0})`);
          } else {
            debug('Map: Skipping fetch - no events in filtered list');
          }
        }
        return;
      }
      
      // Validate dataset_id when required (for dataset context, we still want to validate dataset_id exists)
      if (context === 'dataset') {
        const { selectedDatasetId } = persistantStore;
        const datasetId = selectedDatasetId();
        if (!datasetId || datasetId <= 0) {
          logError('Map: Cannot fetch data - invalid dataset_id:', datasetId);
          return;
        }
      }
      
      // Ensure description is set before making API call
      getDescription();
      const desc = description();
      
      // Validate that we have a description before making the API call
      if (!desc || desc.trim() === '') {
        if (context === 'fleet') {
          log(`[FleetManeuvers] Cannot fetch data - description is not set. Phase: ${phase()}`);
        }
        logError('Map: Cannot fetch data - description is not set. Phase:', phase());
        return;
      }
      
      if (context === 'fleet') {
        log(`[FleetManeuvers] fetchData proceeding - description: ${desc}, phase: ${phase()}`);
      }
      
      // filtered() contains event IDs (numbers) - extract them safely
      if (eventIds.length === 0) {
        if (context === 'fleet') {
          log(`[FleetManeuvers] No valid event IDs found in filtered list`);
        }
        debug('Map: No valid event IDs found in filtered list');
        return;
      }
      
      if (context === 'fleet') {
        log(`[FleetManeuvers] fetchData proceeding - eventIds.length: ${eventIds.length}`);
      }

      // Always use event_list so map data matches table filtering (same event set).
      // Service chunks when >100 events to avoid URL length limits.
      // Track geometry is immutable per event for a given eventType+description, so only
      // ids missing from the geometry cache are fetched — a live merge poll that adds one
      // maneuver costs one small request instead of re-downloading every track on the map.
      const cacheScope = `${persistantStore.selectedProjectId()}|${resolvedEventType()}|${desc}`;
      if (cacheScope !== geometryCacheScope) {
        geometryCacheScope = cacheScope;
        geometryCache.clear();
      }
      const missingIds = eventIds.filter((id) => !geometryCache.has(id));
      debug('Map: eventIds.length =', eventIds.length, 'missing from geometry cache =', missingIds.length);

      const fetchParams: any = {
        eventType: resolvedEventType(),
        description: desc,
        eventList: missingIds,
      };

      if (context === 'fleet') {
        log(`[FleetManeuvers] fetchParams before calling fetchMapData:`, {
          hasEventList: !!fetchParams.eventList,
          eventListLength: fetchParams.eventList?.length || 0,
          hasTimeRange: !!fetchParams.timeRange,
          timeRange: fetchParams.timeRange,
          hasFilters: !!fetchParams.filters,
          filters: fetchParams.filters,
          description: fetchParams.description,
          eventType: fetchParams.eventType
        });
      } else {
        debug('Map: fetchParams before calling fetchMapData:', {
          hasEventList: !!fetchParams.eventList,
          eventListLength: fetchParams.eventList?.length || 0,
          hasTimeRange: !!fetchParams.timeRange,
          hasFilters: !!fetchParams.filters
        });
      }

      if (missingIds.length > 0) {
        const fetchedData = await fetchMapData(context, fetchParams);
        for (const item of fetchedData) {
          const id = Number(item?.event_id);
          if (Number.isFinite(id)) geometryCache.set(id, item);
        }
        while (geometryCache.size > GEOMETRY_CACHE_MAX) {
          const oldest = geometryCache.keys().next().value;
          if (oldest === undefined) break;
          geometryCache.delete(oldest);
        }
      }
      // Assemble the full event set from cache (ids the server returned no geometry for
      // are simply absent and will be retried on the next update).
      const json_data: any[] = eventIds
        .map((id) => geometryCache.get(id))
        .filter((item) => item != null);

      // Always log for debugging - check context value
      log(`[Map] fetchData called - context: ${context}, json_data length: ${json_data?.length || 0}, fetched: ${missingIds.length}`);

      if (json_data && json_data.length > 0) {
        latlngarray = [];
        const dataArray: MapTrackPoint[][] = [];

        let filtered_data = json_data.filter(function (d: { event_id: number }) {
          return isFiltered(d.event_id) && !isEventHidden(d.event_id);
        });

        // Log map data counts for fleet context
        if (context === 'fleet') {
          log(`[FleetManeuvers] Map data fetched: ${json_data.length} maneuvers from API, ${filtered_data.length} after filtering`);
        } else {
          debug(`[Map] Map data fetched: ${json_data.length} maneuvers from API, ${filtered_data.length} after filtering (context: ${context})`);
        }

        const orderRank = new Map(eventIds.map((id, i) => [id, i]));
        filtered_data.sort(
          (a: { event_id: number }, b: { event_id: number }) =>
            (orderRank.get(a.event_id) ?? 0) - (orderRank.get(b.event_id) ?? 0)
        );

        const tableByEventId = new Map<number, Record<string, unknown>>();
        for (const row of resolvedTableRows()) {
          const eid = row.event_id;
          if (typeof eid === "number") tableByEventId.set(eid, row);
        }

        const evt = resolvedEventType();
        const usePortStbd = evt === 'TAKEOFF' || evt === 'BEARAWAY' || evt === 'ROUNDUP';
        filtered_data.forEach((item) => {
          const tableRow = tableByEventId.get(item.event_id);
          const processedData = item.json.values.map((d: Record<string, string | undefined>) => {
            let tackValue: string | undefined;
            if (usePortStbd) {
              const twa = item.twa_entry ?? item.twa_build ?? item.Twa_start ?? 0;
              tackValue = Number(twa) > 0 ? 'STBD' : 'PORT';
            } else if (phase() == 'FULL' || phase() == 'TURN') {
              tackValue = item.twa_entry > 0 ? 'S - P' : 'P - S';
            } else if (phase() == 'INVESTMENT') {
              tackValue = item.twa_entry > 0 ? 'STBD' : 'PORT';
            } else {
              tackValue = item.twa_entry > 0 ? 'PORT' : 'STBD';
            }

            // Convert race_number -1 to 'TRAINING'
            const raceValue = item.Race_number ?? item.race_number ?? item.race ?? item.Race;
            const convertedRace = (raceValue === -1 || raceValue === '-1') ? 'TRAINING' : raceValue;

            const newItem: MapTrackPoint = {
              event_id: item.event_id,
              tws_bin: item.tws_bin,
              tack: tackValue,
              vmg_perc_avg: item.vmg_perc_avg,
              race: convertedRace,
              source_name: item.source_name || '',
              State: item.State,
              Config: item.Config,
              time: parseFloat(String(d.time)),
              hdg: parseFloat(String(d.hdg)),
              lat: parseFloat(String(d.lat)),
              lng: parseFloat(String(d.lng)),
              twa: parseFloat(String(d.twa)),
              LatLng: new L.LatLng(parseFloat(String(d.lng)), parseFloat(String(d.lat))),
            };
            assignYearEventMetadataOnPoint(
              newItem as Record<string, unknown>,
              item as Record<string, unknown>,
              tableRow
            );

            if (newItem.LatLng) latlngarray.push(newItem.LatLng);
            return newItem; 
          });
        
          dataArray.push(processedData); 
        });

        setMapData(dataArray);
        // Flatten to points and build color grouping for downstream consumers
        const flat = dataArray.flat();
        const { groups } = buildColorGrouping(flat, color());
        setColorGroups(groups);
        // Compute representative event_id per group for grouped rendering
        const reps = new Set<number>();
        groups.forEach((grp: ColorGroup) => {
          const first = grp.items && grp.items.length > 0 ? grp.items[0] : null;
          if (first && first.event_id !== undefined && first.event_id !== null) reps.add(first.event_id);
        });
        setGroupRepIds(reps);
        
        // Log final map data counts for fleet context
        if (context === 'fleet') {
          log(`[FleetManeuvers] Map data processed: ${dataArray.length} tracks, ${flat.length} total points`);
        } else {
          debug(`[Map] Map data processed: ${dataArray.length} tracks, ${flat.length} total points (context: ${context})`);
        }
        
        updateMinMaxRanges()

        return dataArray;
      } else {
        latlngarray = [];
        return [];
      }
    } catch (error: any) {
      logError("Error fetching data:", error);
      return [];
    }
  };

  function reinitMap() {
    try {
      if (!map) return;
      const mc = map.getContainer() as HTMLElement | null;
      const layoutEl =
        (mc && mc.closest(".maneuver-map-container")) ||
        d3.select("#map-area").node() ||
        mc;
      if (!layoutEl || !((layoutEl as HTMLElement).offsetWidth >= 0)) return;
      // Layout px, not getBoundingClientRect: #media-container scales the page with --scale-factor and
      // a post-transform read would feed a shrunken size back into layout (double scale).
      mapWidth = (layoutEl as HTMLElement).offsetWidth;
      mapHeight = (layoutEl as HTMLElement).offsetHeight;

      const root = mc?.closest(".maneuver-map-container");
      if (root) {
        d3.select(root)
          .style("width", "100%")
          .style("height", "100%");
        if (mc) d3.select(mc).style("width", "100%").style("height", "100%");
      } else {
        d3.selectAll(".maneuver-map-container")
          .style("width", "100%")
          .style("height", "100%");
        d3.selectAll(".maneuver-map")
          .style("width", "100%")
          .style("height", "100%");
      }

      svg = d3.select(map.getPanes().overlayPane).select("svg");
      svg.selectAll("g").remove();

      g = svg.append("g").attr("width", mapWidth).attr("height", mapHeight);
    } catch (error: any) {
      logError('🗺️ Map.jsx: reinitMap error', error);
    }
  }

  function initMap() {
    try {
      const elId = maneuverMapElementId();
      const maneuverMapElement = document.getElementById(elId);
      if (!maneuverMapElement) return;

      const layoutEl =
        maneuverMapElement.closest(".maneuver-map-container") ||
        d3.select("#map-area").node() ||
        maneuverMapElement;
      // Layout px — see reinitMap.
      mapWidth = (layoutEl as HTMLElement).offsetWidth;
      mapHeight = (layoutEl as HTMLElement).offsetHeight;

      const mapboxTiles = L.tileLayer('', {
        attribution: ''
      });

      const root = maneuverMapElement.closest(".maneuver-map-container");
      if (root) {
        d3.select(root)
          .style("width", "100%")
          .style("height", "100%");
        d3.select(maneuverMapElement)
          .style("width", "100%")
          .style("height", "100%");
      } else {
        d3.selectAll(".maneuver-map-container")
          .style("width", "100%")
          .style("height", "100%");
        d3.selectAll(".maneuver-map")
          .style("width", "100%")
          .style("height", "100%");
      }

      map = L.map(elId, { minZoom: 0, maxZoom: 24, zoomControl: true })
        .addLayer(mapboxTiles)
        .setView([0, 0], zoom_level) as unknown as LeafletMap;

      L.svg({ interactive: true }).addTo(map);

      svg = d3.select(map.getPanes().overlayPane).select("svg");

      svg.attr("pointer-events", "auto")
        .attr("z-index", 900);

      svg.selectAll("g").remove();
      g = svg.append("g").attr("width", mapWidth).attr("height", mapHeight)
        .attr("z-index", 1000);

      d3.selectAll(".leaflet-attribution-flag").remove();

      map.on("dblclick", function (_e: unknown) {
        centerMap(latlngarray);
      });
    } catch (error: any) { /* empty */ 
    }
  }

  const toMapLinePath = d3
    .line<MapTrackPoint>()
    .defined(function (d) {
      if (!d || !d.LatLng) return false;
      if (!map) return false;
      try {
        const point = map.latLngToLayerPoint(d.LatLng);
        return point && !isNaN(point.x) && !isNaN(point.y) && isFinite(point.x) && isFinite(point.y);
      } catch {
        return false;
      }
    })
    .x(function (d) {
      if (!map || !d || !d.LatLng) return 0;
      try {
        const point = map.latLngToLayerPoint(d.LatLng);
        return isNaN(point.x) ? 0 : point.x;
      } catch {
        return 0;
      }
    })
    .y(function (d) {
      if (!map || !d || !d.LatLng) return 0;
      try {
        const point = map.latLngToLayerPoint(d.LatLng);
        return isNaN(point.y) ? 0 : point.y;
      } catch {
        return 0;
      }
    });

  function applyLatLngToLayer(d: { lat: number; lng: number }) {
    if (!map || !d) {
      return { x: 0, y: 0 };
    }
    try {
      const point = map.latLngToLayerPoint(d);
      if (isNaN(point.x) || isNaN(point.y) || !isFinite(point.x) || !isFinite(point.y)) {
        return { x: 0, y: 0 };
      }
      return point;
    } catch {
      return { x: 0, y: 0 };
    }
  }

  function centerMap(arr: { lat: number; lng: number }[]) {
    try {
      if (arr != undefined && arr.length > 0 && map) {
        const bounds = new L.LatLngBounds(arr);
        map.fitBounds(bounds);
      }
    } catch {
      // ignore invalid bounds
    }
  }

  function invalidatePendingRecenter(): void {
    centerToken += 1;
    if (centerTimeout != null) {
      clearTimeout(centerTimeout);
      centerTimeout = null;
    }
  }

  function scheduleRecenter(tracks: MapTrackPoint[][], sourceEventIds?: readonly number[]): void {
    const points = collectFiniteLatLngs(tracks);
    const drawnEventIds = sourceEventIds ?? positiveEventIdsFromTracks(tracks);
    const fit = shouldRecenterManeuverMap({
      shouldCenter: shouldCenterOnNextDraw,
      firstDraw,
      drawnEventIds,
      drawnPointCount: points.length,
      staleEventIds: recenterStaleEventIds,
    });
    if (!fit) {
      if ((shouldCenterOnNextDraw || firstDraw) && points.length > 0) {
        debug("Map: holding recenter until the new maneuver type is drawn", {
          drawnEvents: drawnEventIds.length,
          staleEvents: recenterStaleEventIds?.size ?? 0,
        });
      }
      return;
    }
    const token = centerToken;
    if (centerTimeout != null) clearTimeout(centerTimeout);
    centerTimeout = setTimeout(() => {
      centerTimeout = null;
      if (token !== centerToken || !map) return;
      const fittedNewType = recenterStaleEventIds != null;
      centerMap(points);
      shouldCenterOnNextDraw = false;
      recenterStaleEventIds = null;
      firstDraw = false;
      if (fittedNewType) notifyManeuverMapRecentered();
    }, 100);
  }

  // Capture the ids of the type that is about to be replaced. The selection clear refetches
  // the map before the new type's rows exist; fitting that draw would leave the next type off-screen.
  createEffect(
    on(
      resolvedEventType,
      (next, prev) => {
        if (prev == null || next === prev) return;
        invalidatePendingRecenter();
        shouldCenterOnNextDraw = true;
        recenterStaleEventIds = new Set(untrack(() => resolvedEventIds()));
        debug("Map: maneuver type changed; recenter once the new events are drawn", {
          from: prev,
          to: next,
          previousEventCount: recenterStaleEventIds.size,
        });
      },
      { defer: true }
    )
  );

  function updateMinMaxRanges() {
    let channel = 'tws_bin'
    if (color() == 'VMG') {
      channel = 'vmg_perc_avg'
    }

    minVal = 9999999
    maxVal = -9999999
    mapdata().forEach(function (item: MapTrackPoint[]) {
      try {
        const d = item[0];
        const val = parseFloat(String(d[channel as keyof MapTrackPoint] ?? ""));
        if (val > maxVal) {maxVal = val}
        if (val < minVal) {minVal = val} 
      } catch { /* empty */ 
      }
    })

    InitScales()  
  }

  function InitScales() {
    if (color() === 'TWS') //TWS
    {
        myColorScale = d3.scaleThreshold()
        myColorScale.domain([12.5, 17.5, 22.5, 27.5, 32.5, 37.5, 42.5, 47.5])
        myColorScale.range(["blue","lightblue","cyan","lightgreen","yellow","orange","red","darkred","purple"])
    }
    else if (color() === 'VMG') //VMG
    {
        myColorScale = d3.scaleLinear()
        myColorScale.domain([minVal, (minVal + maxVal) / 2, maxVal])
        myColorScale.range(["blue","lightgrey","red"])
    }
    else if (color() === 'TACK') //TACK
    {
        myColorScale = d3.scaleThreshold()
        myColorScale.domain([-180,-1,1,180])
        myColorScale.range(["red","red","#64ed64","#64ed64"])
    }
    else if (color() === 'MAINSAIL') //MAINSAIL
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'MAINSAIL');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'HEADSAIL') //HEADSAIL
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'HEADSAIL');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'RACE') //RACE
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'RACE');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'SOURCE') //SOURCE (fleet context)
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'SOURCE');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'STATE') //STATE
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'STATE');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'CONFIG') //CONFIG
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'CONFIG');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'CREW') //CREW
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'CREW');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'TESTS')
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'TESTS');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'YEAR') //YEAR
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'YEAR');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'EVENT') //EVENT
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'EVENT');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else
    {
      myColorScale = d3.scaleLinear()
      // myColorScale.domain([4, 8, 14, 18, 22])
      myColorScale.domain([8, 16, 28, 36, 44]);
      myColorScale.range(["yellow","orange","red"])
    }
  }

  // Get the original color based on current color-by setting (without selection-based coloring)
  function getOriginalColor(d: MapTrackPoint | undefined): string {
    if (color() === 'TWS') {
      if (d != undefined) {
        const val = d.tws_bin;
        return (myColorScale as (v: number) => string)(Number(val));
      }
      return "grey";
    }
    if (color() === 'VMG') {
      if (d != undefined) {
        const val = d.vmg_perc_avg;
        return (myColorScale as (v: number) => string)(Number(val));
      }
      return "grey";
    }
    if (color() === 'TACK') {
      if (d != undefined) {
        if (d.tack == 'PORT') {
          return "red";
        } else if (d.tack == 'STBD') {
          return "#64ed64";
        } else if (d.tack == 'P - S') {
          return "red";
        }
        return "#64ed64";
      }
      return "grey";
    }
    if (color() === 'MAINSAIL') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'HEADSAIL') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'RACE') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'SOURCE') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'STATE') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'CONFIG') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'CREW' || color() === 'TESTS') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'YEAR') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'EVENT') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    return "grey";
  }

  function getColor(d: MapTrackPoint | undefined): string {
    if (!d) return "grey";
    return resolveItemColor(d as Record<string, unknown>, {
      selectedIds: highlightedEventIds(),
      legendSelectionActive: legendSelectionActive(),
      colorMode: color(),
      getDimensionColor: (item) => getOriginalColor(item as MapTrackPoint | undefined),
    });
  }

  /**
   * The baseline path(s) to draw: ONE in the mirrored (Normalized) view, where both tacks fold onto
   * the same side and average into a single track, and one per tack in Standard view, where they are
   * mirror-image ground tracks that cannot honestly be merged. The loader decides which it populated,
   * so this just draws whichever is there.
   */
  function getBaselineMapTracks(): Array<{ side: "port" | "stbd" | "all"; points: ManeuverBaselineMapPoint[] }> {
    if (!maneuverBaselineStore.baselineActive()) return [];
    // While a load is in flight the store still holds the previous TWS bin's path, and drawing it
    // would put a baseline on screen for a wind speed the user has already left. Guarded here rather
    // than only at the call sites, since DrawMap and the pan/zoom path both come through here.
    if (maneuverBaselineStore.baselineLoading()) return [];
    const data = maneuverBaselineStore.baselineTracks();
    if (!data) return [];
    if (data.path.length > 1) {
      return [{ side: "all", points: data.path }];
    }
    return [
      { side: "port", points: data.pathsBySide.port },
      { side: "stbd", points: data.pathsBySide.stbd },
    ];
  }

  /**
   * Draw (or redraw) the baseline tracks into the overlay group.
   *
   * Removes first, so calling it again on zoom re-projects rather than stacking a second copy — the
   * paths carry no bound data, so they cannot be re-projected the way `.mapPath` is.
   */
  function drawBaselineTracks(plotG: d3.Selection<SVGGElement, unknown, any, any>): void {
    plotG.selectAll(".maneuver-baseline-track").remove();
    const color = maneuverBaselineStore.baselineColor();
    getBaselineMapTracks().forEach(({ side, points }) => {
      if (!points || points.length < 2) return;
      // Match the maneuver tracks' LatLng convention in fetchData: (lng, lat) argument order.
      const pathPoints = points.map((p) => ({ ...p, LatLng: new L.LatLng(p.lng, p.lat) })) as MapTrackPoint[];
      const pathString = toMapLinePath(pathPoints);
      if (!pathString || pathString.includes("NaN")) return;
      plotG
        .append("path")
        .attr("class", baselineTrackCssClass(side === "port" ? "port" : undefined))
        .attr("data-baseline-side", side)
        .style("stroke", color)
        .attr("d", pathString);
    });
  }

  /**
   * The tracks are fetched, so they land after DrawMap has already run. Redraw just the baseline
   * when they do — going through updateMap would refetch every maneuver track to add one path, and
   * it is guarded against concurrent runs anyway.
   */
  createEffect(() => {
    maneuverBaselineStore.baselineActive();
    maneuverBaselineStore.baselineLoading();
    maneuverBaselineStore.baselineTracks();
    maneuverBaselineStore.baselineColor();
    if (!mapInitialized()) return;
    const layer = g;
    if (!layer || !layer.node()) return;
    untrack(() => drawBaselineTracks(layer));
  });

  /** Same stroke color as .mapPath — boats use this so fill always matches the track */
  function standardTrackStrokeColor(firstPoint: MapTrackPoint | undefined, eventId: number): string {
    const selected = highlightedEventIds();
    const hasSel = selected.length > 0;
    const isSelected = hasSel && isEventSelected(eventId);
    if (!hasSel) return getColor(firstPoint);
    if (!isSelected) return UNSELECTED_TRACK_COLOR;
    return getColor(firstPoint);
  }

  /**
   * Nearest track point to a maneuver-relative seconds value. Track `point.time` is the same field
   * and units as the maneuver timeseries x-axis, so the playhead needs no datetime conversion.
   * Returns null when the nearest sample is further away than PLAYHEAD_MATCH_TOLERANCE_SEC, so a
   * playhead parked outside a track's range draws nothing rather than snapping to an endpoint.
   */
  function nearestPointAtSeconds(points: MapTrackPoint[], sec: number): MapTrackPoint | null {
    if (!points?.length) { return null; }
    let best: MapTrackPoint | null = null;
    let bestDist = Infinity;
    for (const p of points) {
      const t = Number(p?.time);
      if (!Number.isFinite(t)) { continue; }
      const dist = Math.abs(t - sec);
      if (dist < bestDist) { bestDist = dist; best = p; }
    }
    if (!best || bestDist > PLAYHEAD_MATCH_TOLERANCE_SEC) { return null; }
    return best;
  }

  /**
   * Draw/move one boat per selected maneuver at the timeseries playhead time. Deliberately separate
   * from the 5-second interval boats: its class contains no `boat` token, so the blanket `.boat`
   * removals in DrawMap/doUpdates and the interval-boat fade timeouts leave it alone.
   * Pass null to clear (no playhead, or nothing selected).
   */
  function updatePlayheadBoats(sec: number | null) {
    const layer = g;
    if (!layer || !map) { return; }

    if (sec == null || !Number.isFinite(sec)) {
      layer.selectAll(`.${PLAYHEAD_BOAT_CLASS}`).remove();
      return;
    }

    const selected = highlightedEventIds();
    const tracks = maneuverPathsData.filter(
      (m) => selected.length === 0 || isEventSelected(m.eventId)
    );
    if (tracks.length === 0) {
      layer.selectAll(`.${PLAYHEAD_BOAT_CLASS}`).remove();
      return;
    }

    const boatPath = getBoatPathForZoom(zoom_level);
    const seenIds = new Set<string>();

    for (const track of tracks) {
      const point = nearestPointAtSeconds(track.points, sec);
      // Use point.LatLng as-is: it is built as new L.LatLng(d.lng, d.lat) (args swapped), so
      // rebuilding it from point.lat/point.lng would put the boat in the wrong place.
      if (!point?.LatLng) { continue; }
      const { x, y } = applyLatLngToLayer(point.LatLng);
      if (isNaN(x) || isNaN(y)) { continue; }

      const id = `${PLAYHEAD_BOAT_CLASS}-${String(track.eventId).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
      seenIds.add(id);
      const hdg = Number(point.hdg ?? 0) - 180;
      const transform = `translate(${x},${y}) rotate(${hdg})`;
      const fill = standardTrackStrokeColor(track.firstPoint, track.eventId);

      const existing = layer.select(`#${escapeSelectorId(id)}`);
      if (existing.empty()) {
        layer
          .append("path")
          .attr("id", id)
          .attr("class", PLAYHEAD_BOAT_CLASS)
          .attr("d", boatPath)
          .attr("transform", transform)
          .style("fill", fill)
          .style("pointer-events", "none");
      } else {
        existing.attr("d", boatPath).attr("transform", transform).style("fill", fill);
      }
    }

    // Drop boats for tracks that no longer resolve a point (deselected, or playhead out of range).
    layer.selectAll(`.${PLAYHEAD_BOAT_CLASS}`).each(function () {
      const node = this as SVGPathElement;
      if (!seenIds.has(node.id)) { node.remove(); }
    });
  }

  // Reposition the playhead boat as the timeseries playhead is dragged. Reads only the playhead
  // signal and the selection — never triggers a map rebuild, so scrubbing stays cheap.
  createEffect(() => {
    const sec = maneuverPlayheadSeconds();
    highlightedEventIds();
    untrack(() => updatePlayheadBoats(sec));
  });

  function DrawMap(maneuvers: MapTrackPoint[][]) {
    // Verify map is initialized before drawing
    if (!mapInitialized() || !map) {
      debug('🗺️ Map.jsx: DrawMap - Map is not initialized!');
      return;
    }

    // Verify g element exists and is attached, recreate if missing
    const gNode = g && g.node ? g.node() : null;
    const gInDom = gNode && gNode.parentNode;

    if (!g || !gNode || !gInDom) {
      // Try to recreate the g element if it's missing
      try {
        svg = d3.select(map.getPanes().overlayPane).select("svg");
        if (svg.node()) {
          // Remove any existing g elements
          svg.selectAll("g").remove();
          // Recreate the g element
          g = svg.append("g").attr("width", mapWidth).attr("height", mapHeight)
            .attr("z-index", 1000);
          debug('🗺️ Map.jsx: DrawMap - Recreated G element');
        } else {
          logError('🗺️ Map.jsx: DrawMap - SVG element is missing, cannot recreate G element!');
          return;
        }
      } catch (error: any) {
        logError('🗺️ Map.jsx: DrawMap - Failed to recreate G element:', error);
        return;
      }
    }

    if (!g) {
      logError('🗺️ Map.jsx: DrawMap - G element is missing');
      return;
    }
    const plotG = g;

    // Clear all pending boat fade timeouts when starting a new draw
    boatFadeTimeouts.forEach(timeout => clearTimeout(timeout));
    boatFadeTimeouts = [];
    
    d3MapPane().select("svg").selectAll(".line").remove()
    d3MapPane().select("svg").selectAll(".solid_line").remove()
    d3MapPane().select("svg").selectAll(".map_dash_line").remove()
    d3MapPane().select("svg").selectAll(".mapPath").remove()
    d3MapPane().select("svg").selectAll(".boat").interrupt().remove()
    d3MapPane().select("svg").selectAll(".boat-circle").interrupt().remove()
    d3MapPane().select("svg").selectAll(".hover-circle").remove()

    const click = function (event: MouseEvent, d: MapTrackPoint) {
      if (!d) return;

      const id = d.event_id ?? (d.eventId as number | undefined);
      if (id == null) return;

      event.stopPropagation();

      setLegendSelectionActive(false);
      let newSelectedEvents: number[] = [];
      setSelectedEvents((prev) => {
        newSelectedEvents = prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id];
        return newSelectedEvents;
      });

      setTriggerSelection(true);

      if (newSelectedEvents.length > 0) {
        setHasSelection(true);
      } else {
        const hasBrushRange = selectedRange() && selectedRange().length > 0;
        if (!hasBrushRange) {
          setHasSelection(false);
          setSelection([]);
        }
      }
    };

    // Mouse event handlers
    // Offset tooltip left of cursor so it stays left on any monitor (e.g. big display)
    const TOOLTIP_LEFT_OFFSET_PX = 500;
    const mouseover = (event: MouseEvent, d: MapTrackPoint) => {
      const tooltipContent = getTooltipContent(d); 

      setTooltip({
        visible: true,
        content: tooltipContent,
        x: event.pageX - TOOLTIP_LEFT_OFFSET_PX,
        y: event.pageY - 100
      });
    };

    const mousemove = (event: MouseEvent, d: MapTrackPoint) => {
      const tooltipContent = getTooltipContent(d); 

      setTooltip({
        visible: true,
        content: tooltipContent,
        x: event.pageX - TOOLTIP_LEFT_OFFSET_PX,
        y: event.pageY - 100
      });
    };

    const mouseout = () => {
      setTooltip({
        visible: false,
        content: "",
        x: 0,
        y: 0
      });
    }; 

    const getTooltipContent = (point: MapTrackPoint | undefined) => {
      if (!point) return "";
  
      // Check if we have State/Config (GP50)
      const hasStateConfig = point.State !== undefined || point.Config !== undefined;
      
      // Use template literals to build an HTML table dynamically
      let tooltipRows = `
              <tr><td>EVENT ID:</td><td>${point.event_id}</td></tr>`;
      
      // Add source_name if available (for fleet/historical contexts)
      if (point.source_name) {
        tooltipRows += `
              <tr><td>SOURCE:</td><td>${point.source_name}</td></tr>`;
      }
      
      tooltipRows += `
              <tr><td>TWS BIN:</td><td>${point.tws_bin}</td></tr>`;
      
      if (hasStateConfig) {
        // GP50: Show State and Config
        tooltipRows += `
              <tr><td>STATE:</td><td>${point.State ?? ''}</td></tr>
              <tr><td>CONFIG:</td><td>${point.Config ?? ''}</td></tr>
              <tr><td>CREW:</td><td>${point.Crew ?? point.crew ?? point.CREW_CODE ?? ''}</td></tr>
              <tr><td>TESTS:</td><td>${point.Tests ?? point.tests ?? point.TEST ?? 'NONE'}</td></tr>`;
      }
      
      tooltipRows += `
              <tr><td>RACE:</td><td>${point.race ?? ''}</td></tr>`;
      
      // Only show DATE if it exists in the data
      if (point.date !== undefined && point.date !== null && point.date !== '') {
        tooltipRows += `
              <tr><td>DATE:</td><td>${point.date}</td></tr>`;
      }
      
      // TIME should be in the data - use it directly
      if (point.time !== undefined && point.time !== null && String(point.time) !== '') {
        tooltipRows += `
              <tr><td>TIME:</td><td>${point.time}</td></tr>`;
      }
      
      tooltipRows += `
              <tr><td>TWA:</td><td>${point.twa ?? ''}</td></tr>
              <tr><td>VMG_PERCENT:</td><td>${point.vmg_perc_avg ?? ''}</td></tr>`;
      
      return `<table class='table-striped'>${tooltipRows}</table>`;  
    };

    maneuverPathsData = [];
    let boatdataArray: [MapTrackPoint, MapTrackPoint][] = [];

    if (maneuvers != undefined) {
      const maneuverPaths: ManeuverPathRecord[] = [];

      maneuvers.forEach(function (maneuver: MapTrackPoint[]) {
        if (!maneuver || maneuver.length === 0) return;
        
        const eventId = maneuver[0].event_id;
        if (!eventId) return;
        
        // Filter points to remove null/invalid LatLng and validate coordinates
        const validPoints = maneuver.filter(d => {
          if (!d || !d.LatLng) return false;
          // Validate LatLng is a valid Leaflet LatLng object or has lat/lng properties
          if (d.LatLng.lat !== undefined && d.LatLng.lng !== undefined) {
            const lat = d.LatLng.lat;
            const lng = d.LatLng.lng;
            return !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);
          }
          return false;
        });
        if (validPoints.length < 2) return; // Need at least 2 points for a line
        
        maneuverPaths.push({
          eventId: eventId,
          points: validPoints,
          firstPoint: validPoints[0]
        });
      });
      
      // Separate selected and non-selected paths
      const selectedPaths: ManeuverPathRecord[] = [];
      const nonSelectedPaths: ManeuverPathRecord[] = [];

      maneuverPaths.forEach((maneuver) => {
        if (isEventSelected(maneuver.eventId)) {
          selectedPaths.push(maneuver);
        } else {
          nonSelectedPaths.push(maneuver);
        }
      });

      // Render non-selected paths first, then selected paths on top
      const orderedPaths = [...nonSelectedPaths, ...selectedPaths];

      const hasSelection = highlightedEventIds().length > 0;

      // Bind to paths using eventId as key
      const pathsSelection = plotG
        .selectAll(".mapPath")
        .data(orderedPaths, (d: unknown) => (d as ManeuverPathRecord).eventId);

      pathsSelection
        .enter()
        .append("path")
        .attr("class", (d: ManeuverPathRecord) => `mapPath ${getClass(d.firstPoint)}`)
        .attr("data-event-id", (d: ManeuverPathRecord) => d.eventId) // Store eventId as attribute for lookup
        .merge(pathsSelection as unknown as d3.Selection<SVGPathElement, ManeuverPathRecord, SVGGElement, unknown>)
        .attr("data-event-id", (d: ManeuverPathRecord) => d.eventId) // Ensure update selection also has the attribute
        .style("stroke", (d: ManeuverPathRecord) => standardTrackStrokeColor(d.firstPoint, d.eventId))
        .style("stroke-width", (d: ManeuverPathRecord) => {
          if (!hasSelection) return 1;
          const isSelected = isEventSelected(d.eventId);
          return isSelected ? 3 : 0.5;
        })
        .style("stroke-opacity", (d: ManeuverPathRecord) => {
          if (!hasSelection) return 1;
          const isSelected = isEventSelected(d.eventId);
          return isSelected ? 1 : 0.3;
        })
        .style("stroke-linecap", "round")
        .style("fill", "none")
        .style("pointer-events", "none") // Disable pointer events on path, circles will handle it
        .attr("d", (d: ManeuverPathRecord) => {
          if (!map || !d || !d.points) return "";
          const pathString = toMapLinePath(d.points);
          // Validate path string doesn't contain NaN
          if (pathString && !pathString.includes("NaN")) {
            return pathString;
          }
          return "";
        });

      // Reorder paths in DOM so selected paths render on top
      // In SVG, elements that appear later in DOM are rendered on top
      if (hasSelection && selectedPaths.length > 0) {
        const parent = plotG.node();
        if (parent) {
          // Collect all path nodes with their selection status
          const pathNodes: { node: Element; isSelected: boolean }[] = [];
          pathsSelection.each(function (d: unknown) {
            const rec = d as ManeuverPathRecord;
            const node = this as SVGPathElement;
            const isSelected = isEventSelected(rec.eventId);
            pathNodes.push({ node, isSelected });
          });

          // Separate selected and non-selected
          const nonSelected = pathNodes.filter((p) => !p.isSelected);
          const selected = pathNodes.filter((p) => p.isSelected);

          // Reorder: non-selected first, then selected (selected will render on top)
          [...nonSelected, ...selected].forEach(({ node }) => {
            parent.appendChild(node);
          });
        }
      }

      // Store path data for updates
      maneuverPathsData = orderedPaths;

      // Remove any existing outlines (we don't want outlines)
      plotG.selectAll(".mapPath-outline").remove();

      // The reference goes on after the maneuver tracks so it reads on top of them.
      drawBaselineTracks(plotG);

      // Add invisible circles for hover detection and tooltips
      orderedPaths.forEach((maneuver) => {
        const hoverCircles = plotG.selectAll(`.hover-circle-${maneuver.eventId}`).data(maneuver.points);

        hoverCircles
          .enter()
          .append("circle")
          .attr("class", `hover-circle hover-circle-${maneuver.eventId}`)
          .merge(hoverCircles as unknown as d3.Selection<SVGCircleElement, MapTrackPoint, SVGGElement, unknown>)
          .attr("cx", (d: MapTrackPoint) => {
            if (!d || !d.LatLng || !map) return 0;
            const point = applyLatLngToLayer(d.LatLng);
            return isNaN(point.x) ? 0 : point.x;
          })
          .attr("cy", (d: MapTrackPoint) => {
            if (!d || !d.LatLng || !map) return 0;
            const point = applyLatLngToLayer(d.LatLng);
            return isNaN(point.y) ? 0 : point.y;
          })
          .attr("r", 5) // Hover detection radius
          .style("fill", "transparent")
          .style("stroke", "none")
          .style("pointer-events", "all")
          .style("cursor", "pointer")
          .on("mouseover", mouseover)
          .on("mouseout", mouseout)
          .on("mousemove", mousemove)
          .on("click", click);

        hoverCircles.exit().remove();
      });
      
      // Handle boats — draw every 5 seconds along each track
      orderedPaths.forEach(maneuver => {
        const boatPairs = getBoatPositionsByTime(maneuver.points);
        boatdataArray.push(...boatPairs);
      });

      // Boats will be drawn in doUpdates function

      function doUpdates() {
        if (!map) return;
        zoom_level = map.getZoom();

        // Verify g still exists and is in DOM before drawing
        const currentGNode = g && g.node ? g.node() : null;

        if (!g || !currentGNode) {
          logError('🗺️ Map.jsx: doUpdates - G element is missing!');
          return;
        }

        const layer = g;
        const hasSelection = highlightedEventIds().length > 0;

        // Collect paths and reorder them so selected paths render on top
        const pathNodes: { node: Element; isSelected: boolean; eventId: number }[] = [];

        layer.selectAll(".mapPath").each(function (d: unknown) {
          const node = this as SVGPathElement;

          let maneuver: ManeuverPathRecord | undefined = d as ManeuverPathRecord | undefined;
          if (!maneuver || !maneuver.points) {
            const eventIdAttr = d3.select(node).attr("data-event-id");
            if (eventIdAttr && maneuverPathsData.length > 0) {
              maneuver = maneuverPathsData.find((m) => String(m.eventId) === eventIdAttr);
            }
          }

          if (!maneuver || !maneuver.points) return;

          const eventId = maneuver.eventId;
          const isSelected = hasSelection && isEventSelected(eventId);
          const firstPoint = maneuver.firstPoint;

          if (!firstPoint) return;

          const pathString = toMapLinePath(maneuver.points);
          if (pathString && pathString !== 'M0,0' && !pathString.includes("NaN")) {
            d3.select(node).attr("d", pathString);
          }

          node.style.setProperty("stroke", standardTrackStrokeColor(firstPoint, eventId), "important");
          node.style.setProperty("stroke-width", isSelected ? "3" : (hasSelection ? "0.5" : "1"), "important");
          node.style.setProperty("stroke-opacity", isSelected ? "1" : (hasSelection ? "0.2" : "1"), "important");

          pathNodes.push({ node, isSelected, eventId });
        });

        if (hasSelection && pathNodes.length > 0) {
          const parent = layer.node();
          if (parent) {
            const nonSelected = pathNodes.filter((p) => !p.isSelected);
            const selected = pathNodes.filter((p) => p.isSelected);

            [...nonSelected, ...selected].forEach(({ node }) => {
              parent.appendChild(node);
            });
          }
        }

        // Re-project the baseline too: it carries no bound data, so it is redrawn rather than
        // re-attributed like the maneuver paths above.
        drawBaselineTracks(layer);

        //DRAW BOATS
        layer.selectAll(".boat").interrupt().remove();
        layer.selectAll(".boat-circle").interrupt().remove();

        boatdataArray.forEach(function (boatData) {
          if (!boatData || boatData.length < 2) return;

          const d = boatData[1];
          if (!d || !d.LatLng || !map) return;

          const testPoint = applyLatLngToLayer(d.LatLng);
          if (isNaN(testPoint.x) || isNaN(testPoint.y)) return;

          const boatEventId = d.event_id;
          const isBoatSelected = hasSelection && boatEventId != null && isEventSelected(boatEventId);
          const maneuverForBoat =
            boatEventId != null
              ? maneuverPathsData.find((m) => m.eventId === boatEventId)
              : undefined;
          const trackFirstPoint = maneuverForBoat?.firstPoint ?? d;
          const trackColor = standardTrackStrokeColor(trackFirstPoint, boatEventId ?? 0);

          const x = applyLatLngToLayer(d.LatLng).x;
          const y = applyLatLngToLayer(d.LatLng).y;

          const sanitizedTime = sanitizeTimeForId(d.time);
          const sanitizedEventId = String(d.event_id || '0').replace(/[^a-zA-Z0-9_-]/g, '_');
          let boatId = `boat-${sanitizedEventId}-${sanitizedTime}`;

          boatId = boatId
            .replace(/[^a-zA-Z0-9_-]/g, '_')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');

          if (/^[0-9_-]/.test(boatId)) {
            boatId = 'b' + boatId;
          }

          const existingBoat = layer.select(`#${escapeSelectorId(boatId)}`);

          let boat: d3.Selection<SVGPathElement, MapTrackPoint, SVGGElement, unknown>;
          if (existingBoat.empty()) {
            const currentPhase = phase();

            boat = layer
              .append("path")
              .datum(d)
              .attr("id", boatId)
              .attr("class", "boat")
              .attr("data-phase", currentPhase)
              .attr("data-fade-started", "false")
              .style("stroke", "black")
              .style("fill", trackColor)
              .style("opacity", hasSelection ? (isBoatSelected ? 1 : 0.2) : 1)
              .style("pointer-events", "visiblePainted")
              .on("mouseover", mouseover)
              .on("mouseout", mouseout)
              .on("mousemove", mousemove)
              .on("click", click);

            const hdg = Number(d.hdg ?? 0) - 180;

            const boatPath = getBoatPathForZoom(zoom_level);
            boat.attr("d", boatPath);
            boat.attr("transform", "translate(" + x + "," + y + ") rotate(" + hdg + ")");

            const circleId = `boat-circle-${sanitizedEventId}-${sanitizedTime}`;
            layer
              .append("circle")
              .datum(d)
              .attr("id", circleId)
              .attr("class", "boat-circle")
              .attr("data-phase", currentPhase)
              .attr("cx", x)
              .attr("cy", y)
              .attr("r", 1.5)
              .style("fill", trackColor)
              .style("stroke", "none")
              .style("opacity", 0)
              .style("pointer-events", "visiblePainted")
              .on("mouseover", mouseover)
              .on("mouseout", mouseout)
              .on("mousemove", mousemove)
              .on("click", click);

            const timeoutId = setTimeout(() => {
              const boatNode = boat.node();
              if (!boatNode || !boatNode.parentNode) return;

              const boatPhase = boat.attr("data-phase");
              const currentPhaseNow = phase();
              if (boatPhase !== currentPhaseNow) {
                d3.select(boatNode).remove();
                const circleNode = layer.select(`#${escapeSelectorId(circleId)}`).node();
                if (circleNode) d3.select(circleNode).remove();
                return;
              }

              const fadeStarted = boat.attr("data-fade-started") === "true";
              if (fadeStarted) return;

              boat.attr("data-fade-started", "true");

              boat
                .interrupt()
                .transition()
                .duration(1000)
                .style("opacity", 0)
                .on("end", function (this: SVGPathElement) {
                  const boatPhaseEnd = d3.select(this).attr("data-phase");
                  const currentPhaseEnd = phase();
                  if (boatPhaseEnd !== currentPhaseEnd) {
                    d3.select(this).remove();
                    const circleNode = layer.select(`#${escapeSelectorId(circleId)}`).node();
                    if (circleNode) d3.select(circleNode).remove();
                    return;
                  }

                  const endedBoatNode = this;
                  if (endedBoatNode && endedBoatNode.parentNode) {
                    d3.select(endedBoatNode).remove();
                  }

                  const circleNode = layer.select(`#${escapeSelectorId(circleId)}`).node();
                  if (circleNode instanceof Element && circleNode.parentNode) {
                    const circlePhase = d3.select(circleNode).attr("data-phase");
                    const currentPhaseCircle = phase();
                    if (circlePhase !== currentPhaseCircle) {
                      d3.select(circleNode).remove();
                      return;
                    }

                    d3.select(circleNode)
                      .interrupt()
                      .transition()
                      .duration(500)
                      .style("opacity", hasSelection ? (isBoatSelected ? 1 : 0.2) : 1);
                  }
                });
            }, 2000);

            boatFadeTimeouts.push(timeoutId);
          } else {
            existingBoat.each(function () {
              const existingBoatSel = d3.select(this);
              const hdg = Number(d.hdg ?? 0) - 180;
              const boatPath = getBoatPathForZoom(zoom_level);
              existingBoatSel.attr("d", boatPath);
              existingBoatSel.attr("transform", "translate(" + x + "," + y + ") rotate(" + hdg + ")");
              existingBoatSel.style("fill", trackColor);
            });

            const circleSanitizedTime = sanitizeTimeForId(d.time);
            const circleSanitizedEventId = String(d.event_id || '0').replace(/[^a-zA-Z0-9_-]/g, '_');
            let circleId = `boat-circle-${circleSanitizedEventId}-${circleSanitizedTime}`;

            circleId = circleId
              .replace(/[^a-zA-Z0-9_-]/g, '_')
              .replace(/-+/g, '-')
              .replace(/^-+|-+$/g, '');

            if (/^[0-9_-]/.test(circleId)) {
              circleId = 'b' + circleId;
            }

            const existingCircle = layer.select(`#${escapeSelectorId(circleId)}`);

            if (existingCircle.empty()) {
              layer
                .append("circle")
                .datum(d)
                .attr("id", circleId)
                .attr("class", "boat-circle")
                .attr("cx", x)
                .attr("cy", y)
                .attr("r", 1.5)
                .style("fill", trackColor)
                .style("stroke", "none")
                .style("opacity", 0)
                .style("pointer-events", "visiblePainted")
                .on("mouseover", mouseover)
                .on("mouseout", mouseout)
                .on("mousemove", mousemove)
                .on("click", click);
            } else {
              existingCircle.attr("cx", x).attr("cy", y).style("fill", trackColor);
            }
          }
        });

        layer.selectAll(".hover-circle").each(function (d: unknown) {
          const pt = d as MapTrackPoint;
          if (pt && pt.LatLng && map) {
            const point = applyLatLngToLayer(pt.LatLng);
            if (!isNaN(point.x) && !isNaN(point.y)) {
              d3.select(this as SVGCircleElement).attr("cx", point.x).attr("cy", point.y);
            }
          }
        });

        layer.selectAll(".boat-circle").each(function (d: unknown) {
          const pt = d as MapTrackPoint;
          if (pt && pt.LatLng && map) {
            const point = applyLatLngToLayer(pt.LatLng);
            if (!isNaN(point.x) && !isNaN(point.y)) {
              d3.select(this as SVGCircleElement).attr("cx", point.x).attr("cy", point.y);
            }
          }
        });

        // Re-apply after a redraw (which drops our element) and on zoom (new projection + glyph size).
        // doUpdates is also the zoomend handler, so this covers both.
        updatePlayheadBoats(untrack(() => maneuverPlayheadSeconds()));
      }

      map.on("zoomend", doUpdates);
      doUpdates();
      
      // Fit after the first draw, a phase change, or a maneuver-type change — but not the
      // draw that still contains the previous type's events (see scheduleRecenter).
      scheduleRecenter(maneuvers);
      } 

    if (highlightedEventIds().length > 0) {
      updateSelection();
    }
  }

  function drawFeatures() {
    if (!map || !svg || !g) {
      return;
    }
    
    function doUpdates() {
      if (!map || !svg || !g) return;
      
      // Check if map is fully loaded before accessing bounds
      if (!map._loaded || !map._container) return;
      
      svg.selectAll("line.crosstrack").remove()

      let zoom_level;
      try {
        zoom_level = map.getZoom();
      } catch (e) {
        // Map not ready yet
        return;
      }

      if (zoom_level > 2) {
        // Get current map bounds with a buffer
        let bounds;
        try {
          bounds = map.getBounds();
          // Check if bounds is valid by checking if it has required methods
          if (!bounds || typeof bounds.getSouth !== 'function' || 
              typeof bounds.getNorth !== 'function' || 
              typeof bounds.getWest !== 'function' || 
              typeof bounds.getEast !== 'function') {
            return;
          }
        } catch (e) {
          // Map bounds not available yet
          return;
        }
        
        const buffer = 0.01; // ~1km buffer in degrees
        const minLat = bounds.getSouth() - buffer;
        const maxLat = bounds.getNorth() + buffer;
        const minLon = bounds.getWest() - buffer;
        const maxLon = bounds.getEast() + buffer;
        
        const baseLat = 39.476984;
        const baseLon = -0.291140;
        // const latint = 0.000224956; //25m increments
        const latint = 0.000899872; //100m increments

        // Calculate start and end indices based on visible bounds
        const startIndexNorth = Math.max(0, Math.floor((minLat - baseLat) / latint));
        const endIndexNorth = Math.ceil((maxLat - baseLat) / latint);
        
        const startIndexSouth = Math.max(0, Math.floor((baseLat - maxLat) / latint));
        const endIndexSouth = Math.ceil((baseLat - minLat) / latint);

        // Draw northward lines (only those in visible range)
        for (let i = startIndexNorth; i <= endIndexNorth && i < 1000; i++) {
          if (i < 0) continue;
          
          let lat = baseLat + (i * latint);
          
          // Only draw if line is within visible bounds
          if (lat >= minLat && lat <= maxLat) {
            let fromLatLng = new L.LatLng(lat, baseLon - 0.2)
            let toLatLng = new L.LatLng(lat, baseLon + 0.2)
            
            // Check if line endpoints are within visible longitude range
            if ((baseLon - 0.2 <= maxLon && baseLon - 0.2 >= minLon) || 
                (baseLon + 0.2 <= maxLon && baseLon + 0.2 >= minLon) ||
                (baseLon - 0.2 <= minLon && baseLon + 0.2 >= maxLon)) {
              
              g.append('line')
                .attr("class", "crosstrack")
                .style("stroke", "lightgray")
                .style("stroke-width", 1)
                .attr("x1", applyLatLngToLayer(fromLatLng).x)
                .attr("y1", applyLatLngToLayer(fromLatLng).y)
                .attr("x2", applyLatLngToLayer(toLatLng).x)
                .attr("y2", applyLatLngToLayer(toLatLng).y)
            }
          }
        }

        // Draw southward lines (only those in visible range)
        for (let i = startIndexSouth; i <= endIndexSouth && i < 1000; i++) {
          if (i < 0) continue;
          
          let lat = baseLat - (i * latint);
          
          // Only draw if line is within visible bounds
          if (lat >= minLat && lat <= maxLat) {
            let fromLatLng = new L.LatLng(lat, baseLon - 0.2)
            let toLatLng = new L.LatLng(lat, baseLon + 0.2)
            
            // Check if line endpoints are within visible longitude range
            if ((baseLon - 0.2 <= maxLon && baseLon - 0.2 >= minLon) || 
                (baseLon + 0.2 <= maxLon && baseLon + 0.2 >= minLon) ||
                (baseLon - 0.2 <= minLon && baseLon + 0.2 >= maxLon)) {
              
              g.append('line')
                .attr("class", "crosstrack")
                .style("stroke", "lightgray")
                .style("stroke-width", 1)
                .attr("x1", applyLatLngToLayer(fromLatLng).x)
                .attr("y1", applyLatLngToLayer(fromLatLng).y)
                .attr("x2", applyLatLngToLayer(toLatLng).x)
                .attr("y2", applyLatLngToLayer(toLatLng).y)
            }
          }
        }
      }
    } 

    if (map) {
      // Wait for map to be fully loaded before attaching listeners
      const setupListeners = () => {
        // Check if map is ready
        if (!map || !map._loaded || !map._container) {
          return;
        }
        
        // Map is loaded, attach event listeners (only once)
        // Check if listeners are already attached to avoid duplicates
        if (!map._crosstrackListenersAttached) {
          map.on("zoomend", doUpdates);
          map.on("load", doUpdates); // Also update when map loads/reloads
          map._crosstrackListenersAttached = true;
        }
        
        // Call doUpdates once map is ready
        doUpdates();
      };
      
      // Start setup - will wait for load if needed
      if (map._loaded && map._container) {
        // Map already loaded, setup immediately
        setupListeners();
      } else {
        // Wait for map to load
        map.once("load", setupListeners);
        // Also try after a short delay in case load event already fired
        setTimeout(() => {
          if (map && map._loaded && map._container && !map._crosstrackListenersAttached) {
            setupListeners();
          }
        }, 100);
      }
    }
  }

  function updateSelection() {
    // Use untrack to prevent updateMinMaxRanges from creating reactive dependencies
    untrack(() => {
      updateMinMaxRanges();
    });

    if (!g || !g.node()) {
      return;
    }

    const ug = g;
    const hasSelection = highlightedEventIds().length > 0;

    // Collect paths for reordering
    const pathNodes: { node: Element; isSelected: boolean; eventId: number }[] = [];

    ug.selectAll(".mapPath").each(function (d: unknown) {
      if (!d) return;

      const node = this as SVGPathElement;
      const maneuver = d as ManeuverPathRecord;
      const eventId = maneuver.eventId;
      const isSelected = hasSelection && isEventSelected(eventId);
      const firstPoint = maneuver.firstPoint;

      if (!firstPoint) return;

      const selected = highlightedEventIds();
      node.style.setProperty("stroke", standardTrackStrokeColor(firstPoint, eventId), "important");
      node.style.setProperty("stroke-width", isSelected ? "3" : (hasSelection ? "0.5" : "1"), "important");
      node.style.setProperty("stroke-opacity", isSelected ? "1" : (hasSelection ? "0.2" : "1"), "important");

      const outline = node.parentNode?.querySelector(`.mapPath-outline[data-event-id="${eventId}"]`);
      if (outline) {
        d3.select(outline as Element).remove();
      }

      pathNodes.push({ node, isSelected, eventId });
    });
    
    // Reorder paths in DOM so selected paths render on top
    // In SVG, elements that appear later in DOM are rendered on top
    if (hasSelection && pathNodes.length > 0) {
      const parent = ug.node();
      if (parent) {
        const nonSelected = pathNodes.filter((p) => !p.isSelected);
        const selected = pathNodes.filter((p) => p.isSelected);

        [...nonSelected, ...selected].forEach(({ node }) => {
          parent.appendChild(node);
        });
      }
    }

    ug.selectAll(".boat").each(function (d: unknown) {
      const pt = d as MapTrackPoint;
      if (!pt || !pt.event_id) return;
      const boatEventId = pt.event_id;
      const isBoatSelected = hasSelection && boatEventId && isEventSelected(boatEventId);
      const fp = maneuverPathsData.find((m) => m.eventId === boatEventId)?.firstPoint ?? pt;
      const tc = standardTrackStrokeColor(fp, boatEventId);
      d3.select(this)
        .style("fill", tc)
        .style("opacity", hasSelection ? (isBoatSelected ? 1 : 0.2) : 1);
    });

    ug.selectAll(".boat-circle").each(function (d: unknown) {
      const pt = d as MapTrackPoint;
      if (!pt || !pt.event_id) return;
      const boatEventId = pt.event_id;
      const isBoatSelected = hasSelection && boatEventId && isEventSelected(boatEventId);
      const fp = maneuverPathsData.find((m) => m.eventId === boatEventId)?.firstPoint ?? pt;
      const tc = standardTrackStrokeColor(fp, boatEventId);
      d3.select(this)
        .style("fill", tc)
        .style("opacity", hasSelection ? (isBoatSelected ? 1 : 0.2) : 1);
    });

    setTriggerSelection(false);

    // Banner/hasSelection reflects MANUAL selection only; the live highlight layer must not set it.
    if (selectedEvents().length > 0) {
      setHasSelection(true);
    } else {
      // Don't clear hasSelection when the other panel has a brush selection (e.g. map timeseries brushed in split view)
      const hasBrushRange = selectedRange() && selectedRange().length > 0;
      if (!hasBrushRange) {
        setHasSelection(false);
        setSelection([]);
      }
    }
  }

  function getClass(d: MapTrackPoint) {
    if (d.tack  == 'PORT') {
        return "map_dash_line"
    } else if (d.tack == 'STBD') {
      return "solid_line"
    } else if (d.tack == 'P - S') {
        return "map_dash_line"
    } else {
        return "solid_line"
    }
  }

  let containerRef: HTMLDivElement | undefined;
  let resizeObserver: ResizeObserver | undefined;

  function resizeChart() {
    if (!map) return;
    reinitMap();
    map.invalidateSize();
    drawFeatures();
    // Do not call updateMap() here — ResizeObserver/layout after a draw would refetch in a loop.
    if (updateInProgress) return;
    const cached = mapdata();
    if (cached && cached.length > 0) {
      untrack(() => {
        DrawMap(cached);
        updateSelection();
      });
    }
  }

  /** Clears triggerUpdate, syncs filtered hash tracking, queues updateMap — shared by trigger effect and post-fetch retry. */
  let consumeTriggerAndQueueUpdateMap: (() => void) | undefined;

  const updateMap = () => {
    if (context === 'fleet') {
      log(`[FleetManeuvers] updateMap() called - updateInProgress: ${updateInProgress}`);
    }
    // Guard: prevent concurrent updates
    if (updateInProgress) {
      if (context === 'fleet') {
        log(`[FleetManeuvers] updateMap() - update already in progress, returning`);
      }
      return;
    }
    
    // Guard: ensure map is initialized before updating
    if (!mapInitialized() || !map) {
      if (context === 'fleet') {
        log(`[FleetManeuvers] updateMap called but map is not initialized yet`);
      }
      debug('Map: updateMap called but map is not initialized yet');
      return;
    }
    
    // Guard: don't update if filtered data is not available yet
    const eventIds = resolvedEventIds();
    if (eventIds.length === 0) {
      if (context === 'fleet') {
        log(`[FleetManeuvers] updateMap called but filtered data is not available yet - eventIds.length: 0`);
      }
      debug('Map: updateMap called but filtered data is not available yet');
      return;
    }
    
    if (context === 'fleet') {
      log(`[FleetManeuvers] updateMap proceeding - eventIds.length: ${eventIds.length}`);
    }
    
    // Check if eventType or phase has changed - if so, center map on next draw and clear boats.
    // A type change also records the current ids (see the event-type effect) so this draw is not
    // fitted when it still shows the previous type.
    const currentEventType = resolvedEventType();
    const currentPhase = phase();
    if (lastEventType !== null && (lastEventType !== currentEventType || lastPhase !== currentPhase)) {
      invalidatePendingRecenter();
      shouldCenterOnNextDraw = true;
      
      // Clear all pending boat fade timeouts
      boatFadeTimeouts.forEach(timeout => clearTimeout(timeout));
      boatFadeTimeouts = [];
      
      // Completely remove all boats and boat circles when phase/eventType changes
      // Use direct SVG selection to ensure clearing works even if g is not available
      d3MapPane().select("svg").selectAll(".boat").interrupt().remove();
      d3MapPane().select("svg").selectAll(".boat-circle").interrupt().remove();
      // Also clear via g if available
      if (g && g.node()) {
        g.selectAll(".boat").interrupt().remove();
        g.selectAll(".boat-circle").interrupt().remove();
      }
    }
    lastEventType = currentEventType;
    lastPhase = currentPhase;
    
    updateInProgress = true;
    setLoading(true);
    fetchData().then((data) => {
      if (!map) return;
      DrawMap(data || []);
      updateSelection();
      setFirstLoad(false);
      updateInProgress = false;
      if (props?.instanceId) {
        setLoading(false);
      } else {
        setTimeout(() => {
          if (!map) return;
          setLoading(false);
        }, 500);
      }
      if (triggerUpdate()) {
        consumeTriggerAndQueueUpdateMap?.();
      }
    }).catch((error) => {
      if (map) {
        logError('🗺️ Map.jsx: Error in updateMap fetchData', error);
        setLoading(false);
      }
      updateInProgress = false;
      if (triggerUpdate()) {
        consumeTriggerAndQueueUpdateMap?.();
      }
    });
  };

  // Use createEffect to ensure DOM is ready before initializing
  createEffect(() => {
    // Only initialize once
    if (mapInitialized()) return;
    
    const maneuverMapElement = document.getElementById(maneuverMapElementId());
    
    if (maneuverMapElement && !map) {
      // DOM is ready, initialize map
      initMap();
      
      // Only call drawFeatures and updateMap if map was successfully initialized
      if (map) {
        setMapInitialized(true);
        // Small delay to ensure Leaflet has fully initialized
        setTimeout(() => {
          drawFeatures();
          // First data fetch is driven by triggerUpdate / filtered createEffects only — avoids duplicate fetchMapData with init + debounced watcher.
        }, 100);
      }
    }
  });

  // Event types to target for a tag write - a single selected type, else all maneuver types.
  const maneuverEventTypes = (): string[] => {
    const currentEventType = resolvedEventType();
    return currentEventType ? [currentEventType] : MANEUVER_EVENT_TYPES;
  };

  // Shared post-write handling for GRADE and STATE updates.
  const afterTagUpdate = () => {
    // Clear selected events after a successful tag update
    setSelectedEvents([]);
    setHasSelection(false);
    setSelection([]);
    setTriggerSelection(true);

    // Trigger parent component to refetch data
    if (props?.onDataUpdate && typeof props.onDataUpdate === 'function') {
      props.onDataUpdate();
    } else {
      // Fallback: trigger update
      setTriggerUpdate(true);
    }
  };

  // Function to perform the GRADE update
  const performGradeUpdate = async (gradeValue: string, selected: number[]) => {
    // Get required values from persistent store
    const { selectedClassName, selectedProjectId } = persistantStore;
    const className = selectedClassName();
    const projectId = selectedProjectId();

    if (!className || !projectId) {
      logError('Cannot update GRADE: missing class_name or project_id');
      return;
    }

    const eventTypes = maneuverEventTypes();

    try {
      // Call admin API to update GRADE for selected events
      const response = await putData(`${apiEndpoints.admin.events}/tags`, {
        class_name: className,
        project_id: projectId,
        events: selected,
        event_types: eventTypes,
        key: 'GRADE',
        value: gradeValue
      });
      
      if (response.success) {
        debug(`Successfully updated GRADE to ${gradeValue} for ${selected.length} event(s)`);
        
        // Grade update in HuniDB will be implemented when available
        // Currently grade updates are handled on next data fetch
        
        // Update grade filter to include the newly graded value
        const currentGrades = selectedGradesManeuvers();
        const gradeValueStr = String(gradeValue);
        if (!currentGrades.includes(gradeValueStr)) {
          setSelectedGradesManeuvers([...currentGrades, gradeValueStr]);
          debug(`Added grade ${gradeValue} to selectedGrades filter`);
        }

        afterTagUpdate();
      } else {
        logError('Failed to update GRADE:', response.message || 'Unknown error');
      }
    } catch (error: any) {
      logError('Error updating GRADE:', error);
    }
  };

  onMount(() => {
    // Fallback init for maneuver window / popup: DOM may not be ready when createEffect first ran.
    requestAnimationFrame(() => {
      if (mapInitialized() || map) return;
      const el = document.getElementById(maneuverMapElementId());
      if (!el) return;
      initMap();
      if (!map) return;
      setMapInitialized(true);
      drawFeatures();
    });

    resizeObserver = new ResizeObserver((_entries) => {
      resizeChart();
    });

    if (containerRef) {
      resizeObserver.observe(containerRef);
    }

    // Fallback to window resize event
    window.addEventListener('resize', resizeChart);
    
    // Add keyboard listener for GRADE updates (0-5 keys)
    const handleKeyPress = (event: KeyboardEvent) => {
      try {
        // Don't trigger if user is typing in an input field or textarea
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
          return;
        }
        
        // Only handle if there are selected events
        const selected = selectedEvents();
        if (!selected || selected.length === 0) {
          return;
        }
        
        const key = event.key;
        if (shouldBlockGradeLikeHotkeysForPerformanceSelections(key)) {
          return;
        }

        if (!['0', '1', '2', '3', '4', '5'].includes(key)) {
          return;
        }

        // Check if user is NOT a reader (readers cannot grade)
        const currentUser = user();
        if (!currentUser) {
          return;
        }
        
        // Superusers can always grade
        if (currentUser.is_super_user) {
          // Allow grading
        } else {
          // Check if user is a reader - readers cannot grade
          const userPermissions = currentUser.permissions;
          let isReader = false;
          
          if (typeof userPermissions === 'string') {
            isReader = userPermissions === 'reader';
          } else if (typeof userPermissions === 'object' && userPermissions !== null) {
            const permissionValues = Object.values(userPermissions);
            // User is a reader if ALL their permissions are 'reader'
            isReader = permissionValues.length > 0 && permissionValues.every(p => p === 'reader');
          }
          
          if (isReader) {
            return; // Readers cannot grade
          }
        }
        
        // Prevent default behavior and stop propagation
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Show confirmation dialog before proceeding
        const message = `Are you sure you want to update GRADE to ${parseInt(key, 10)} for ${selected.length} selected event(s)?\n\nThis action will modify the data and cannot be easily undone.`;

        // Use window.confirm - it automatically closes when user clicks OK or Cancel
        const confirmed = window.confirm(message);
        if (!confirmed) {
          // User cancelled - dialog is already closed
          return;
        }

        // User confirmed - proceed with the update
        // Call asynchronously to avoid blocking the keydown handler
        setTimeout(() => {
          const update = performGradeUpdate(String(parseInt(key, 10)), selected);
          update.catch((error) => {
            logError('Error in maneuver hotkey update:', error);
          });
        }, 0);
      } catch (error: any) {
        logError('Error in handleKeyPress:', error);
        event.preventDefault();
        event.stopPropagation();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);

    // onCleanup, not a returned function: Solid discards onMount return values, so these listeners
    // and the ResizeObserver used to survive unmount (the sibling onCleanup above already worked).
    onCleanup(() => {
      window.removeEventListener('resize', resizeChart);
      window.removeEventListener('keydown', handleKeyPress);
      if (resizeObserver && containerRef) {
        resizeObserver.unobserve(containerRef);
        resizeObserver.disconnect();
      }
    });
  });

  let lastFilteredCount = 0;
  let lastFilteredHash = '';
  let filteredUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

  const getMapFilteredHash = (currentFiltered: unknown): string => {
    if (!Array.isArray(currentFiltered) || currentFiltered.length === 0) return '';
    const nums = currentFiltered
      .map((item: unknown) =>
        typeof item === 'number' ? item : (item as { event_id?: number })?.event_id ?? item
      )
      .filter((id): id is number => typeof id === 'number' && id > 0);
    if (nums.length === 0) return '';
    const sorted = [...nums].sort((a, b) => a - b);
    return `${sorted.length}:${sorted.join(',')}`;
  };

  consumeTriggerAndQueueUpdateMap = () => {
    setTriggerUpdate(false);
    if (filteredUpdateTimeout) {
      clearTimeout(filteredUpdateTimeout);
      filteredUpdateTimeout = null;
    }
    const arr = resolvedFilteredSnapshot();
    const n = Array.isArray(arr) ? arr.length : 0;
    if (n > 0) {
      lastFilteredHash = getMapFilteredHash(arr);
      lastFilteredCount = n;
    }
    queueMicrotask(() => {
      if (untrack(() => updateInProgress)) return;
      untrack(() => updateMap());
    });
  };

  createEffect(() => {
    const ids = resolvedEventIds();
    props?.eventList;
    props?.tableRows;
    const isReady = untrack(() => mapInitialized() && map);
    if (!isReady || ids.length > 0) return;
    untrack(() => {
      setMapData([]);
      setLoading(false);
      setFirstLoad(false);
      if (g && g.node()) {
        g.selectAll('*').remove();
      }
    });
  });

  createEffect(() => {
    if (!isPanelActive()) return;
    const hasUpdate = triggerUpdate();
    const isReady = untrack(() => mapInitialized() && map);
    const inProgress = untrack(() => updateInProgress);

    if (!hasUpdate || !isReady) return;
    if (inProgress) {
      return;
    }
    consumeTriggerAndQueueUpdateMap();
  });

  // Watch for filtered data changes - update map when data becomes available and map is ready
  // This serves as a backup in case triggerUpdate doesn't fire or gets cleared
  // Use debouncing to handle rapid re-renders during initial load
  createEffect(() => {
    if (!isPanelActive()) return;
    const currentFiltered = resolvedFilteredSnapshot();
    const currentFilteredCount = Array.isArray(currentFiltered) ? currentFiltered.length : 0;
    // Track instance event list / global filtered for per-cell live maps
    props?.eventList;
    props?.tableRows;
    props?.maneuverEventType;
    // For fleet context, also watch selectedSources to trigger refetch when sources change
    const sources = context === 'fleet' ? selectedSources() : [];
    const isReady = untrack(() => mapInitialized() && map);
    const hasTriggerUpdate = untrack(() => triggerUpdate());
    
    // Log for fleet context to debug why map isn't updating
    if (context === 'fleet') {
      log(`[FleetManeuvers] Map createEffect triggered - filteredCount: ${currentFilteredCount}, isReady: ${isReady}, hasTriggerUpdate: ${hasTriggerUpdate}, sources.length: ${sources?.length || 0}`);
    }
    
    // Use untrack to check updateInProgress to prevent reactive dependency
    const inProgress = untrack(() => updateInProgress);
    
    const filteredHash = getMapFilteredHash(currentFiltered);
    
    // Clear any pending timeout
    if (filteredUpdateTimeout) {
      clearTimeout(filteredUpdateTimeout);
      filteredUpdateTimeout = null;
    }
    
    // Reset when filtered data is cleared
    if (currentFilteredCount === 0) {
      if (context === 'fleet') {
        log(`[FleetManeuvers] Map createEffect - filtered data is empty, returning early`);
      }
      lastFilteredCount = 0;
      lastFilteredHash = '';
      return;
    }
    
    // Only update if:
    // 1. Map is ready
    // 2. We have filtered data
    // 3. Data actually changed (hash changed OR count changed from 0)
    // 4. triggerUpdate is not set (let that handle the update instead)
    // 5. Update is not already in progress
    const justGotData = lastFilteredCount === 0 && currentFilteredCount > 0;
    const dataChanged = filteredHash !== lastFilteredHash || justGotData;

    if (isReady && currentFilteredCount > 0 && dataChanged && !hasTriggerUpdate && !inProgress) {
      const delay = justGotData ? 50 : 100;
      filteredUpdateTimeout = setTimeout(() => {
        // Re-check conditions after timeout (they might have changed)
        const stillReady = untrack(() => mapInitialized() && map);
        const stillHasTrigger = untrack(() => triggerUpdate());
        const stillInProgress = untrack(() => updateInProgress);
        const currentHash = getMapFilteredHash(resolvedFilteredSnapshot());
        
        if (stillReady && !stillHasTrigger && !stillInProgress && currentHash === filteredHash && currentHash !== lastFilteredHash) {
          if (context === 'fleet') {
            log(`[FleetManeuvers] Map createEffect - calling updateMap() - hash: ${currentHash}`);
          }
          // Use untrack to prevent this update from triggering reactive effects
          untrack(() => {
            updateMap();
          });
          lastFilteredHash = filteredHash;
        } else {
          if (context === 'fleet') {
            log(`[FleetManeuvers] Map createEffect - NOT calling updateMap - stillReady: ${stillReady}, stillHasTrigger: ${stillHasTrigger}, stillInProgress: ${stillInProgress}, currentHash: ${currentHash}, filteredHash: ${filteredHash}, lastFilteredHash: ${lastFilteredHash}`);
          }
        }
        
        filteredUpdateTimeout = null;
      }, delay);
    }
    
    lastFilteredCount = currentFilteredCount;
    if (filteredHash && !filteredUpdateTimeout) {
      lastFilteredHash = filteredHash;
    }
  });
  
  // Cleanup timeout on unmount
  onCleanup(() => {
    if (filteredUpdateTimeout) {
      clearTimeout(filteredUpdateTimeout);
      filteredUpdateTimeout = null;
    }
  });

  createEffect(() => {
    if (triggerSelection()) {
      // Use untrack to prevent updateSelection from creating reactive dependencies
      untrack(() => {
        updateSelection();
      });
    }
  });

  // Watch for selectedEvents changes (from cross-window sync) and update selection
  createEffect(() => {
    void highlightedEventIds();
    const isReady = untrack(() => mapInitialized() && map && svg && g);
    
    // Update selection when selectedEvents changes and map is ready
    // Use untrack to prevent updateSelection from creating reactive dependencies
    if (isReady) {
      untrack(() => {
        updateSelection();
      });
    }
  });

  // Watch for cutEvents and isCut changes - update map when cuts are cleared or applied
  let lastCutEventsLength = 0;
  let lastIsCut = false;
  let lastFilteredLength = 0;
  let cutUpdateTimeout: NodeJS.Timeout | null = null;
  createEffect(() => {
    // Access cutEvents and isCut to trigger effect when they change
    const currentCutEvents = cutEvents();
    const currentIsCut = isCut();
    const currentCutEventsLength = currentCutEvents?.length || 0;
    const currentFiltered = resolvedFilteredSnapshot();
    const currentFilteredLength = Array.isArray(currentFiltered) ? currentFiltered.length : 0;
    const isReady = untrack(() => mapInitialized() && map);
    const inProgress = untrack(() => updateInProgress);
    
    // Only update map when cut state actually changes (cuts cleared or applied)
    // This prevents unnecessary updates while ensuring the map redraws when cuts are cleared
    const cutsChanged = currentCutEventsLength !== lastCutEventsLength || currentIsCut !== lastIsCut;
    const cutsWereCleared = (lastCutEventsLength > 0 && currentCutEventsLength === 0) || (lastIsCut && !currentIsCut);
    
    if (isReady && !inProgress && cutsChanged) {
      debug('Map: cutEvents or isCut changed, triggering map update', {
        cutEventsLength: currentCutEventsLength,
        isCut: currentIsCut,
        previousCutEventsLength: lastCutEventsLength,
        previousIsCut: lastIsCut,
        cutsWereCleared,
        filteredLength: currentFilteredLength,
        previousFilteredLength: lastFilteredLength
      });
      
      // When cuts are cleared, wait for isCut to become false AND filtered() to be updated
      // Track the filtered length when cuts were active, so we can detect when it changes
      if (cutsWereCleared) {
        // Clear any pending timeout first
        if (cutUpdateTimeout) {
          clearTimeout(cutUpdateTimeout);
          cutUpdateTimeout = null;
        }
        
        const cutDataLength = lastFilteredLength > 0 ? lastFilteredLength : currentFilteredLength; // Remember the cut data size
        const checkForClearedState = (attempt: number, maxAttempts: number = 10) => {
          const checkIsCut = isCut();
          const checkCutEvents = cutEvents();
          const checkFiltered = resolvedFilteredSnapshot();
          const cutEventsEmpty = !checkCutEvents || checkCutEvents.length === 0;
          const isCutFalse = !checkIsCut;
          const filteredLength = checkFiltered?.length || 0;
          // filtered() is ready if it has more events than the cut data, OR if isCut is false and it's non-empty
          const filteredUpdated = isCutFalse && filteredLength > 0 && (filteredLength > cutDataLength || cutDataLength === 0);
          
          debug(`Map: Checking cleared state (attempt ${attempt}/${maxAttempts})`, {
            isCut: checkIsCut,
            isCutFalse,
            cutEventsLength: checkCutEvents?.length || 0,
            cutEventsEmpty,
            filteredLength: filteredLength,
            cutDataLength: cutDataLength,
            filteredUpdated
          });
          
          // Both isCut must be false AND cutEvents must be empty AND filtered() must be updated
          if (isCutFalse && cutEventsEmpty && filteredUpdated) {
            debug('Map: Cuts fully cleared, filtered() updated, triggering map update', {
              filteredLength: filteredLength,
              isCut: checkIsCut
            });
            // Use untrack to prevent updateMap from creating reactive dependencies
            untrack(() => {
              updateMap();
            });
            cutUpdateTimeout = null;
          } else if (attempt < maxAttempts) {
            // Not ready yet, wait and retry
            const delay = attempt <= 2 ? 100 : 200; // First 2 retries after 100ms, then 200ms intervals
            debug(`Map: Cuts not fully cleared yet (isCut: ${checkIsCut}, cutEvents: ${checkCutEvents?.length || 0}, filtered: ${filteredLength}), retrying in ${delay}ms...`);
            cutUpdateTimeout = setTimeout(() => {
              checkForClearedState(attempt + 1, maxAttempts);
            }, delay);
          } else {
            // Max attempts reached, force update anyway
            debug('Map: Max retry attempts reached, forcing map update');
            untrack(() => {
              updateMap();
            });
            cutUpdateTimeout = null;
          }
        };
        
        // Start checking after a short delay to allow state to propagate
        cutUpdateTimeout = setTimeout(() => {
          checkForClearedState(1);
        }, 100);
      } else {
        // Cuts were applied (not cleared), update immediately
        // Clear any pending timeout
        if (cutUpdateTimeout) {
          clearTimeout(cutUpdateTimeout);
          cutUpdateTimeout = null;
        }
        // Use untrack to prevent updateMap from creating reactive dependencies
        untrack(() => {
          updateMap();
        });
      }
    }
    
    // Update tracking variables
    lastCutEventsLength = currentCutEventsLength;
    lastIsCut = currentIsCut;
    lastFilteredLength = currentFilteredLength;
  });
  
  // Cleanup timeout on unmount
  onCleanup(() => {
    if (cutUpdateTimeout) {
      clearTimeout(cutUpdateTimeout);
      cutUpdateTimeout = null;
    }
  });

  // Watch for sourcesStore changes when using SOURCE coloring - rebuild color scale when sources load
  createEffect(() => {
    const currentColor = color();
    if (currentColor !== 'SOURCE') {
      return; // Only react when coloring by SOURCE
    }
    
    // Access sourcesStore to trigger effect when sources load
    const sources = sourcesStore.sources();
    const isReady = sourcesStore.isReady();
    
    // If sources just loaded and we have data, rebuild the color scale
    if (isReady && sources.length > 0 && mapdata().length > 0) {
      debug('Map: Sources loaded, rebuilding color scale for SOURCE coloring');
      // Use untrack to prevent InitScales from creating reactive dependencies
      untrack(() => {
        InitScales();
      });
      // Trigger a redraw to apply new colors - use untrack to prevent reactive loop
      const isMapReady = untrack(() => mapInitialized() && map);
      if (isMapReady) {
        untrack(() => {
          updateSelection();
        });
      }
    }
  });

  // Only react to color() — not mapdata (every fetch would re-run and log "Color changed" while scales already refresh in fetchData/DrawMap).
  createEffect(
    on(
      color,
      () => {
        const isReady = untrack(() => mapInitialized() && map && svg && g);
        if (isReady && untrack(() => mapdata().length > 0)) {
          debug('Map: Color changed, rebuilding color scale and updating selection');
          untrack(() => {
            InitScales();
            updateSelection();
          });
        }
      },
      { defer: true }
    )
  );

  onCleanup(() => {
    setMapInitialized(false);
    boatFadeTimeouts.forEach(timeout => clearTimeout(timeout));
    boatFadeTimeouts = [];
    if (centerTimeout != null) {
      clearTimeout(centerTimeout);
      centerTimeout = null;
    }
    
    // Clean up D3 selections and SVG elements
    if (g && g.node()) {
      try {
        g.selectAll("*").remove();
      } catch (error) {
        logError('Map: Error cleaning up D3 selections:', error);
      }
    }
    
    // Clean up SVG overlay
    try {
      d3MapPane().select("svg").selectAll("*").remove();
    } catch (error) {
      // SVG might not exist, ignore
    }
    
    // Remove Leaflet map
    if (map) {
      try {
        map.remove();
        map = null;
      } catch (error) {
        logError('Map: Error removing Leaflet map:', error);
      }
    }

    // Disconnect ResizeObserver
    if (resizeObserver && containerRef) {
      resizeObserver.unobserve(containerRef);
      resizeObserver.disconnect();
      resizeObserver = undefined;
    }

    // Remove window resize event listener
    window.removeEventListener('resize', resizeChart);
    // Note: keydown listener is cleaned up in onMount return function
    
    // Clear data to free memory
    setMapData([]);
    setColorGroups([]);
    setGroupRepIds(new Set<number>());
    latlngarray = [];
    
    debug('Map: Cleanup complete - all resources cleared');
  });

  return (
    <>
      {firstload() && <Loading />}
      <div ref={(el) => { containerRef = el ?? undefined; }} class="maneuver-map-container" style={{
          "opacity": loading() ? 0.2 : 1, 
          "pointer-events": loading() ? "none" : "auto", 
          "transition": "opacity 0.5s ease",
          "width": "100%",
          "height": "100%",
          "display": "flex",
          "flex-direction": "column",
          "padding-right": "25px",
        }}>
        <div id={maneuverMapElementId()} class="maneuver-map" style={{
            "flex": "1",
            "width": "100%",
            "height": "100%",
          }}></div>
        <Show when={!props?.compact}>
          <p class="flex justify-end mt-2">Lines are separated by 100m</p>
        </Show>
        {/* Portalled to <body>: #media-container applies transform: scale(--scale-factor), and a
            transformed ancestor becomes the containing block for position: fixed descendants — the
            tooltip would be both offset and scaled. pageX/pageY match viewport coords here because
            body.scaling-page does not scroll (scroll lives on #main-content). */}
        <Portal>
          <div id="tt" class="tooltip" style={{
              opacity: tooltip().visible ? 1 : 0,
              left: `${tooltip().x}px`,
              top: `${tooltip().y}px`,
              position: "fixed",
              "pointer-events": "none",
              "z-index": 9999,
            }} innerHTML={sanitizeTooltipHtml(tooltip().content)} />
        </Portal>
      </div>
    </>
  );
}
